import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBarcodeRead } from '@fortawesome/pro-regular-svg-icons/faBarcodeRead';

import React, { useState, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Input } from 'ui-components/components/input';
import { Text, TextType } from 'ui-components/components/text';

import { useIsUnitsOfMeasureAvailable } from '../../../../../../../feature-gates/use-is-units-of-measure-available';
import { searchProduct } from '../../../../api/products-api';
import { messages } from '../messages';
import styles from './product-search-input.styles.module.less';
import { SearchableDropdown } from '../searchable-dropdown/searchable-dropdown';

/**
 * @typedef {object} Product
 * @property {string} productId
 * @property {string} name
 * @property {string} sku
 */

/**
 * @module ProductSearchInput
 * @description A component that provides a search input for finding and adding products to a purchase order.
 * This component uses the PurchaseOrderContext to access and update the purchase order state.
 * It allows users to search for products by name or SKU and adds them to the purchase order if they don't already exist.
 * The search can be triggered by clicking the search button or pressing Enter.
 * @returns {JSX.Element} A search input field with a search button
 * @example
 * <ProductSearchInput />
 */
export const ProductSearchInput = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState(/** @type {Product[] | undefined} */ (undefined));
  const [isSearching, setIsSearching] = useState(false);
  const [chosenItemId, setChosenItemId] = useState('');
  const [searchMessage, setSearchMessage] = useState(/** @type {string | undefined} */ (undefined));
  const intl = useIntl();
  const { isAvailable: isUnitsOfMeasureEnabled } = useIsUnitsOfMeasureAvailable();
  const rowKeyCounterRef = useRef(0);
  const getNextRowKey = useCallback((productId) => `${productId}-${++rowKeyCounterRef.current}`, []);

  /**
   * Executes a product search based on the current searchTerm.
   * @returns {Promise<void>}
   */
  const handleSearch = useCallback(async () => {
    const trimmed = searchTerm.trim();

    if (trimmed.length <= 2) {
      setSearchMessage(messages.searchTermError);
      setSearchResults(undefined);
      return;
    }
    setIsSearching(true);

    try {
      const rawProducts = await searchProduct(searchTerm, isUnitsOfMeasureEnabled);
      // If units of measure are enabled, we want to include products that match the search term in their UOM SKUs, even if the product SKU/name doesn't directly match.
      const products = isUnitsOfMeasureEnabled
        ? rawProducts?.map((product) => {
            const term = searchTerm.trim().toLowerCase();
            const directMatch =
              product.sku?.toLowerCase().includes(term) ||
              product.upc?.toLowerCase().includes(term) ||
              product.name?.toLowerCase().includes(term);
            if (directMatch) return product;
            const matchedUomBySku = product.unitsOfMeasure?.find((u) => u.uomSku?.toLowerCase().includes(term));
            if (matchedUomBySku) return { ...product, _matchedUomSku: matchedUomBySku.uomSku };
            const matchedUomByBarcode = product.unitsOfMeasure?.find((u) => u.barcode?.toLowerCase().includes(term));
            if (matchedUomByBarcode) return { ...product, _matchedUomBarcode: matchedUomByBarcode.barcode };
            return product;
          })
        : rawProducts;
      if (products?.length > 0) {
        setSearchResults(products);
        setSearchMessage(undefined);
        if (products.length === 1) {
          setChosenItemId(`${products[0].productId}`);
        }
      } else {
        setSearchMessage(messages.noSkuOrUpcError);
        setSearchResults(undefined);
      }
    } catch {
      setSearchMessage(messages.searchError);
    } finally {
      setIsSearching(false);
    }
  }, [isUnitsOfMeasureEnabled, searchTerm]);

  /**
   * Handles Enter key press in the input field to trigger a search.
   * @param {React.KeyboardEvent<HTMLInputElement>} e
   */
  const handleKeyPress = useCallback(
    async (e) => {
      if (e.key === 'Enter') {
        await handleSearch();
      }
    },
    [handleSearch],
  );

  const onChange = (value) => {
    setSearchTerm(value);
    setSearchResults(undefined);
    setSearchMessage(undefined);
  };

  return (
    <div className={styles['product-search-container']}>
      {searchResults?.length > 0 || searchMessage ? (
        <SearchableDropdown
          chosenItemId={chosenItemId}
          getNextRowKey={getNextRowKey}
          searchMessage={searchMessage}
          searchResults={searchResults}
          setChosenItemId={setChosenItemId}
          setSearchResults={setSearchResults}
          setSearchTerm={setSearchTerm}
        />
      ) : null}

      <div className={styles['search-input-container']}>
        <Input
          className={styles['search-input']}
          name='searchTerm'
          onChange={onChange}
          onKeyDown={handleKeyPress}
          placeholder={intl.formatMessage(messages.productSearch)}
          type='text'
          value={searchTerm}
        />
        <Button className={styles['search-button']} isLoading={isSearching} onClick={handleSearch} theme={THEME_CONSTANTS.unstyled}>
          {!isSearching && <FontAwesomeIcon className={styles['autocomplete-scanner-icon']} icon={faBarcodeRead} />}
        </Button>
      </div>
      <Text className={styles['search-helper-text']} textType={TextType.Paragraph} value={messages.workWithScanner} />
    </div>
  );
};
